import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';

import type { TestConnectionResult } from '../../model/settingsTypes';

export interface TestConnectionButtonProps {
  /** Runs the connection test (a settings- or provider-level BFF call). */
  run: () => Promise<TestConnectionResult>;
  label?: string;
  disabled?: boolean;
  /** Render compactly (text button, no result banner — used inside tables). */
  compact?: boolean;
  onResult?: (result: TestConnectionResult) => void;
}

/**
 * Triggers a connection-test endpoint and surfaces a non-sensitive
 * success/failure banner. Owns its own mutation so it can be dropped anywhere.
 */
export function TestConnectionButton({
  run,
  label,
  disabled = false,
  compact = false,
  onResult,
}: TestConnectionButtonProps) {
  const { t } = useTranslation('settings');
  const mutation = useMutation<TestConnectionResult, NormalizedApiError, void>({
    mutationFn: () => run(),
    onSuccess: (result) => onResult?.(result),
  });

  const result = mutation.data;

  const button = (
    <Button
      variant={compact ? 'text' : 'outlined'}
      size="small"
      disabled={disabled || mutation.isPending}
      onClick={() => mutation.mutate()}
      startIcon={
        mutation.isPending ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <WifiTetheringRoundedIcon />
        )
      }
    >
      {label ?? t('testConnectionButton.label')}
    </Button>
  );

  if (compact) {
    return button;
  }

  return (
    <Stack spacing={1.5} alignItems="flex-start">
      {button}
      {mutation.isError ? (
        <Alert severity="error" icon={<ErrorRoundedIcon />} sx={{ width: '100%' }}>
          {mutation.error.message}
        </Alert>
      ) : null}
      {result ? (
        <Alert
          severity={result.success ? 'success' : 'warning'}
          icon={result.success ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}
          sx={{ width: '100%' }}
        >
          {result.message}
        </Alert>
      ) : null}
    </Stack>
  );
}
